/* oxlint-disable */
/**
 * Chat Markdown Parser
 *
 * This module provides a custom markdown parser for chat messages with support for:
 * - Inline formatting: bold (**), italic (* or _), underline (%%), strikethrough (~~), inline code block (`)
 * - Block formatting: unordered lists, ordered lists, paragraphs, code blocks
 * - URL linkification with trailing punctuation handling
 * - User mentions integration
 * - Search term highlighting
 *
 * The parser uses an AST (Abstract Syntax Tree) approach for accurate nested formatting.
 */

import React from "react";
import { Highlightify } from "@jugl-web/utils/utils/Highlightify";
import { Mentionify } from "@jugl-web/utils/utils/Mentionify";
import { cx, getUniqueId } from "@jugl-web/utils";
import { decodeHTMLEntities } from "@jugl-web/utils/utils/decodeHTMLEntities";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Block-level element types for the markdown parser.
 * Represents different structural elements in the parsed document.
 */
type Block =
  | { type: "p"; content: string } // Paragraph
  | { type: "h"; content: string } // Header (# / ## / ###) — rendered as bold paragraph
  | { type: "ul"; items: string[] } // Unordered list
  | { type: "ol"; items: string[]; start?: number } // Ordered list with optional start number
  | { type: "code"; content: string }; // Code block

/**
 * AST node representing plain text content
 */
type TextNode = { type: "text"; value: string };

/**
 * Supported markdown marker types:
 * - "*": italic (single asterisk)
 * - "**": bold (double asterisk)
 * - "%%": underline (double percent)
 * - "~~": strikethrough (double tilde)
 * - "_": italic (underscore)
 * - "`": inline code block (grave)
 */
type MarkerKind = "*" | "**" | "%%" | "~~" | "_" | "`";

/**
 * AST node representing a formatting marker with nested children
 */
type MarkerNode = { type: "marker"; marker: MarkerKind; children: Node[] };

/**
 * Root node of the AST containing all parsed content
 */
type RootNode = { type: "root"; children: Node[] };

/**
 * Union type for all possible AST nodes
 */
type Node = TextNode | MarkerNode;

const MARKDOWN_TRAILING = /[*_~%]+$/;

// ============================================================================
// Utility Functions for Unclosed Markers
// ============================================================================

/**
 * Converts an unclosed marker node back to its string representation.
 * Used when a formatting marker is never closed, treating it as plain text.
 *
 * Example: If "**hello" is never closed, this returns "**hello"
 *
 * @param node - The marker node to stringify
 * @returns The string representation with marker prefix
 */
const stringifyUnclosed = (node: MarkerNode): string => {
  const inner = node.children
    .map((c) =>
      c.type === "text" ? decodeHTMLEntities(c.value) : stringifyUnclosed(c)
    )
    .join("");
  return node.marker + inner;
};

/**
 * Converts an unclosed marker node into an array of nodes where the marker
 * becomes plain text. Used for flattening unclosed formatting.
 *
 * @param node - The marker node to convert
 * @returns Array of nodes with the marker as text followed by children
 */
const unclosedToNodes = (node: MarkerNode): Node[] => {
  const nodes: Node[] = [{ type: "text", value: node.marker }];
  for (const c of node.children) nodes.push(c);
  return nodes;
};

// ============================================================================
// Main Parser: Text to AST
// ============================================================================

/**
 * Parses markdown text into an Abstract Syntax Tree (AST).
 *
 * The parser uses a stack-based approach to handle nested formatting correctly:
 * 1. Scans text character by character
 * 2. When it encounters a marker character (*, %, ~, _), counts consecutive occurrences
 * 3. Determines if the marker can open or close based on context
 * 4. Matches opening and closing markers using the stack
 * 5. Handles special cases like nested formatting and unclosed markers
 *
 * @param text - The markdown text to parse
 * @returns The root AST node containing the parsed structure
 */
export const parseMarkdownToAST = (text: string): RootNode => {
  const root: RootNode = { type: "root", children: [] };
  const stack: (RootNode | MarkerNode)[] = [root];

  /**
   * Pushes text to the current top of the stack.
   * Merges with the previous text node if possible to reduce node count.
   */
  const pushText = (str: string) => {
    if (!str) return;
    const top = stack[stack.length - 1];
    const last = top.children[top.children.length - 1];
    const value = decodeHTMLEntities(str);
    if (last && last.type === "text") last.value += value;
    else top.children.push({ type: "text", value });
  };

  /**
   * Checks if a marker can open based on surrounding characters.
   * Special-case '_' so that it does NOT open when it's in the middle of a word
   */
  const canOpen = (marker: string, prevChar: string, nextChar: string) => {
    if (marker === "_") {
      return nextChar !== "" && prevChar.trim() === "";
    }
    return nextChar !== "";
  };

  /**
   * Checks if a marker can close based on surrounding characters.
   * Special-case '_' so that it does NOT close when it's in the middle of a word
   */
  const canClose = (marker: string, prevChar: string, nextChar: string) => {
    if (marker === "_") {
      return prevChar !== "" && nextChar.trim() === "";
    }
    return prevChar !== "";
  };

  /**
   * Counts the total number of asterisks remaining in the text from a given position.
   * Used for deciding whether to open or close bold/italic markers.
   */
  const countFutureStars = (pos: number) => {
    let c = 0;
    for (let k = pos; k < text.length; k++) if (text[k] === "*") c++;
    return c;
  };

  /**
   * Sums the length of all currently open star markers (* or **) in the stack.
   * Helps determine if we have enough asterisks to close all open markers.
   */
  const sumOpenStarLengths = () => {
    let s = 0;
    for (let k = 1; k < stack.length; k++) {
      const n = stack[k];
      if (n.type === "marker" && (n.marker === "*" || n.marker === "**")) {
        s += n.marker.length;
      }
    }
    return s;
  };

  /**
   * Attempts to match exactly N asterisks worth of open markers from the top of the stack.
   * Used for complex cases like ***text*** (3 asterisks = ** + *).
   *
   * @param N - Number of asterisks to match
   * @returns Array of marker nodes that sum to N asterisks, or null if no exact match
   */
  const matchMarkersExactTop = (N: number): MarkerNode[] | null => {
    let remaining = N;
    const closers: MarkerNode[] = [];
    for (let k = stack.length - 1; k > 0 && remaining > 0; k--) {
      const node = stack[k];
      if (node.type !== "marker") return null;
      if (node.marker !== "*" && node.marker !== "**") return null;
      const need = node.marker.length;
      if (need > remaining) return null;
      closers.push(node);
      remaining -= need;
    }
    if (remaining === 0) return closers;
    return null;
  };

  /**
   * Checks if a closing marker exists later in the text.
   * Prevents opening markers that will never be closed.
   */
  const hasClosingMarker = (marker: string, from: number): boolean => {
    return text.indexOf(marker, from) !== -1;
  };

  /**
   * Handles non-asterisk markers (underline, strikethrough, italic with underscore).
   * Simpler than asterisk handling because these don't have the bold/italic complexity.
   *
   * @param marker - The marker string to handle
   * @param count - Number of consecutive marker characters found
   * @param prevChar - Character before the marker sequence
   * @param nextChar - Character after the marker sequence
   * @param j - Position after the marker sequence
   */
  const handleOtherMarker = (
    marker: MarkerKind,
    count: number,
    prevChar: string,
    nextChar: string,
    j: number
  ) => {
    const mlen = marker.length;
    let pairs = Math.floor(count / mlen);
    while (pairs > 0) {
      // Try to close existing marker
      if (canClose(marker, prevChar, nextChar)) {
        let matchIndex = -1;
        for (let k = stack.length - 1; k >= 0; k--) {
          const n = stack[k];
          if (n.type === "marker" && n.marker === marker) {
            matchIndex = k;
            break;
          }
        }
        if (matchIndex !== -1) {
          // Close all markers above the matching one (they become plain text)
          while (stack.length - 1 > matchIndex) {
            const node = stack.pop() as MarkerNode;
            const parent = stack[stack.length - 1];
            parent.children.push(...unclosedToNodes(node));
          }
          // Close the matching marker
          const node = stack.pop() as MarkerNode;
          const parent = stack[stack.length - 1];
          parent.children.push(node);
          pairs -= 1;
          continue;
        }
      }
      // Try to open new marker if closing didn't work
      if (canOpen(marker, prevChar, nextChar) && hasClosingMarker(marker, j)) {
        stack.push({ type: "marker", marker, children: [] });
      } else {
        pushText(marker);
      }
      pairs -= 1;
    }
    // Handle any remaining characters that don't form a complete marker
    if (count % mlen !== 0) pushText(marker[0]);
  };

  // ============================================================================
  // Main Parsing Loop
  // ============================================================================

  let i = 0;
  const len = text.length;
  while (i < len) {
    const ch = text[i];

    if (ch === "\\") {
      pushText(text[i + 1]);
      i += 2;
      continue;
    }

    // Check if current character is a marker character
    if (ch === "*" || ch === "%" || ch === "~" || ch === "_" || ch === "`") {
      // Count consecutive marker characters
      let j = i;
      while (j < len && text[j] === ch) j++;
      const count = j - i;
      const prevChar = i > 0 ? text[i - 1] : "";
      const nextChar = j < len ? text[j] : "";

      // ========================================================================
      // Special handling for asterisks (bold and italic)
      // ========================================================================
      if (ch === "*") {
        let stars = count;

        // First, try to close markers with exact match from top of stack
        // Example: If we have "**" and "*" open, and encounter "***", this closes both
        const exactClosers = canClose("*", prevChar, nextChar)
          ? matchMarkersExactTop(stars)
          : null;
        if (exactClosers) {
          for (const node of exactClosers) {
            // Close any improperly nested markers above this one
            while (stack.length > 1 && stack[stack.length - 1] !== node) {
              const popped = stack.pop() as MarkerNode;
              const parent = stack[stack.length - 1];
              parent.children.push({
                type: "text",
                value: stringifyUnclosed(popped),
              });
            }
            // Close the matched marker
            if (stack.length > 1 && stack[stack.length - 1] === node) {
              const popped = stack.pop() as MarkerNode;
              const parent = stack[stack.length - 1];
              parent.children.push(popped);
            }
          }
          i = j;
          continue;
        }

        // Calculate how many stars we could potentially open
        const futureStars = countFutureStars(j);
        const openSum = sumOpenStarLengths();
        const maxAdditionalOpenable = Math.max(0, futureStars - openSum);

        // Special case: inside italic, encountering 2-3 stars, open bold inside
        // Example: *text **bold** text* (bold inside italic)
        const topMarker = stack[stack.length - 1];
        if (
          topMarker &&
          topMarker.type === "marker" &&
          topMarker.marker === "*" &&
          stars >= 2 &&
          stars <= 3 &&
          canOpen("*", prevChar, nextChar) &&
          maxAdditionalOpenable >= 2
        ) {
          let toOpen = Math.min(stars, maxAdditionalOpenable);
          while (toOpen > 0) {
            if (toOpen >= 2) {
              stack.push({ type: "marker", marker: "**", children: [] });
              toOpen -= 2;
              stars -= 2;
            } else {
              stack.push({ type: "marker", marker: "*", children: [] });
              toOpen -= 1;
              stars -= 1;
            }
          }
          if (stars > 0) {
            pushText("*".repeat(stars));
            stars = 0;
          }
          i = j;
          continue;
        }

        // Try to close existing markers from the top of the stack
        while (stars > 0) {
          const top = stack[stack.length - 1];
          if (
            top &&
            top.type === "marker" &&
            (top.marker === "*" || top.marker === "**") &&
            canClose("*", prevChar, nextChar)
          ) {
            const need = top.marker.length;
            if (need <= stars) {
              const popped = stack.pop() as MarkerNode;
              const parent = stack[stack.length - 1];
              parent.children.push(popped);
              stars -= need;
              continue;
            }
          }
          break;
        }

        // If we still have stars remaining, try to open new markers
        if (stars > 0) {
          const futureStars = countFutureStars(j);
          const openSum = sumOpenStarLengths();
          const maxAdditionalOpenable = Math.max(0, futureStars - openSum);
          let toOpen = Math.min(stars, maxAdditionalOpenable);
          while (toOpen > 0) {
            if (toOpen >= 2) {
              stack.push({ type: "marker", marker: "**", children: [] });
              toOpen -= 2;
              stars -= 2;
            } else {
              stack.push({ type: "marker", marker: "*", children: [] });
              toOpen -= 1;
              stars -= 1;
            }
          }
          // Any remaining stars become plain text
          if (stars > 0) {
            pushText("*".repeat(stars));
            stars = 0;
          }
        }

        i = j;
        continue;
      }

      // ========================================================================
      // Handle other markers (%, ~, _, `)
      // ========================================================================
      if (ch === "%") handleOtherMarker("%%", count, prevChar, nextChar, j);
      else if (ch === "~")
        handleOtherMarker("~~", count, prevChar, nextChar, j);
      else if (ch === "_") handleOtherMarker("_", count, prevChar, nextChar, j);
      else if (ch === "`") handleOtherMarker("`", count, prevChar, nextChar, j);

      i = j;
    } else {
      // Not a marker character - scan ahead to the next marker and add as text
      let j = i;
      while (
        j < len &&
        text[j] !== "*" &&
        text[j] !== "%" &&
        text[j] !== "~" &&
        text[j] !== "_" &&
        text[j] !== "`" &&
        text[j] !== "\\"
      )
        j++;
      pushText(text.slice(i, j));
      i = j;
    }
  }

  // Clean up any unclosed markers at the end
  while (stack.length > 1) {
    const node = stack.pop() as MarkerNode;
    const parent = stack[stack.length - 1];
    parent.children.push(...unclosedToNodes(node));
  }

  return root;
};

// ============================================================================
// AST to React Conversion
// ============================================================================

/**
 * Converts AST nodes to React elements.
 * Recursively processes marker nodes to create properly nested HTML elements.
 *
 * @param nodes - Array of AST nodes to convert
 * @returns Array of React elements
 */
const astToReact = (nodes: Node[]): React.ReactNode[] =>
  nodes.map((n) => {
    if (n.type === "text") return n.value;
    const children = astToReact(n.children || []);
    switch (n.marker) {
      case "**":
        return React.createElement("b", { key: getUniqueId() }, children);
      case "*":
      case "_":
        return React.createElement("i", { key: getUniqueId() }, children);
      case "%%":
        return React.createElement("u", { key: getUniqueId() }, children);
      case "~~":
        return React.createElement("s", { key: getUniqueId() }, children);
      case "`":
        return React.createElement("code", { key: getUniqueId() }, children);
      default:
        return React.createElement("span", { key: getUniqueId() }, children);
    }
  });

/**
 * Parses inline markdown text and returns React elements.
 * This is the simpler API for inline-only parsing (no blocks).
 *
 * @param text - The markdown text to parse
 * @returns React elements representing the formatted text
 */
export const parseInline = (text: string): React.ReactNode => {
  const ast = parseMarkdownToAST(text);
  return astToReact(ast.children || []);
};

// ============================================================================
// Block-Level Parsing
// ============================================================================

/**
 * Parses lines into block-level elements (paragraphs, lists).
 * Uses a recursive approach to process line by line.
 *
 * @param lines - Array of text lines to parse
 * @returns Array of block elements
 */
const parseBlocks = (lines: string[]): Block[] => {
  if (lines.length === 0) return [];
  const [first, ...rest] = lines;

  // Multi-line code block
  if (first.startsWith("```")) {
    const codeLines: string[] = [];
    let i = 1; // start od następnej linii, żeby pominąć początkowe ```
    while (i < lines.length && !lines[i].startsWith("```")) {
      codeLines.push(lines[i]);
      i++;
    }
    const remaining = lines.slice(i + 1); // pomijamy końcowe ```
    return [
      { type: "code", content: codeLines.join("\n") },
      ...parseBlocks(remaining),
    ];
  }

  // Check for markdown headers (# / ## / ### etc.) — strip hashes
  if (/^#{1,6}\s+/.test(first)) {
    const content = first.replace(/^#{1,6}\s+/, "");
    return [{ type: "h", content }, ...parseBlocks(rest)];
  }

  // Check for unordered list (starts with -, *, or +)
  if (/^\s*([*+-])\s+/.test(first)) {
    const items: string[] = [];
    for (const line of [first, ...rest]) {
      if (/^\s*([*+-])\s+/.test(line)) {
        items.push(line.replace(/^\s*([*+-])\s+/, ""));
      } else {
        break;
      }
    }
    const remaining = rest.slice(items.length - 1);
    return [{ type: "ul", items }, ...parseBlocks(remaining)];
  }

  // Check for ordered list (starts with number followed by period)
  if (/^\s*\d+\.\s+/.test(first)) {
    const items: string[] = [];
    let start = parseInt(first.match(/^\s*(\d+)\./)![1], 10);
    for (const line of [first, ...rest]) {
      const m = line.match(/^\s*\d+\.\s+/);
      if (m) {
        items.push(line.replace(/^\s*\d+\.\s+/, ""));
      } else break;
    }
    const remaining = rest.slice(items.length - 1);
    return [{ type: "ol", items, start }, ...parseBlocks(remaining)];
  }

  // Default to paragraph
  return [{ type: "p", content: first || " " }, ...parseBlocks(rest)];
};

// ============================================================================
// URL Linkification
// ============================================================================

/**
 * Regular expression for matching HTTP(S) URLs
 */
const URL_RE = /https?:\/\/[^\s<>'"`|)]+/g;

/**
 * Scans the input text for URLs and replaces them with placeholder tokens
 * before markdown parsing. This prevents any trailing markdown markers
 * (*, _, %, ~) from being incorrectly included in the URL. If a URL ends
 * with one or more markdown markers at the end of the line or message,
 * the markers are preserved after the placeholder so they can be parsed
 * normally as formatting.
 *
 * @param text - The input text that may contain URLs
 * @returns An object containing the text with URL placeholders and the array of extracted URLs
 */
const extractUrls = (text: string) => {
  const urls: string[] = [];

  const replaced = text.replace(URL_RE, (match) => {
    const trailingMarkerMatch = match.match(/[*_%~]+$/);
    let cleanUrl = match;
    let trailing = "";

    if (trailingMarkerMatch) {
      trailing = trailingMarkerMatch[0];
      cleanUrl = match.slice(0, -trailing.length);
    }

    const idx = urls.length;
    urls.push(cleanUrl);

    return `{{URL:${idx}}}` + trailing;
  });

  return { replaced, urls };
};

/**
 * Recursively replaces placeholders like {{URL:0}} in React nodes with actual <a> elements.
 * Preserves all existing formatting and nested structure, so markdown styling is not lost.
 * Strings without placeholders are left unchanged. Handles both single nodes and arrays of nodes.
 *
 * @param nodeOrNodes - React node or array of nodes that may contain URL placeholders
 * @param urls - Array of URLs corresponding to the placeholders
 * @param whiteLinks - Optional flag to style links as white for dark backgrounds
 * @returns React nodes with placeholders replaced by clickable <a> elements
 */
const replaceTokensWithLinks = (
  nodeOrNodes: React.ReactNode | React.ReactNode[],
  urls: string[],
  whiteLinks?: boolean
): React.ReactNode[] => {
  const nodes = React.Children.toArray(nodeOrNodes);
  const out: React.ReactNode[] = [];

  for (const node of nodes) {
    if (typeof node === "string" || typeof node === "number") {
      const s = String(node);
      if (!s.includes("{{URL:")) {
        out.push(s);
        continue;
      }

      const parts = s.split(/(\{\{URL:\d+\}\})/g).filter(Boolean);
      for (const part of parts) {
        const match = part.match(/^\{\{URL:(\d+)\}\}$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const href = urls[idx];
          out.push(
            <a
              key={getUniqueId()}
              href={href}
              target="_blank"
              rel="noreferrer"
              className={cx(
                "break-all underline",
                whiteLinks ? "text-white" : "text-primary"
              )}
            >
              {href}
            </a>
          );
        } else {
          out.push(part);
        }
      }
      continue;
    }

    if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
      const children = React.Children.toArray(node.props.children || []);
      const replacedChildren = replaceTokensWithLinks(
        children,
        urls,
        whiteLinks
      );
      out.push(
        React.cloneElement(node, { key: getUniqueId() }, replacedChildren)
      );
      continue;
    }

    out.push(node);
  }

  return out;
};

/**
 * Parses inline markdown text and replaces URLs with clickable links.
 * Extracts URLs and replaces them with safe placeholder tokens, then parses
 * the markdown normally, and finally replaces the placeholders with actual
 * <a> elements while preserving any formatting.
 *
 * @param text - The markdown text to parse
 * @param whiteLinks - Optional flag to style links as white for dark backgrounds
 * @returns React nodes representing the formatted inline content with links
 */
const renderInlineWithLinks = (text: string, whiteLinks?: boolean) => {
  const { replaced, urls } = extractUrls(text);
  const parsed = parseInline(replaced);
  return replaceTokensWithLinks(parsed, urls, whiteLinks);
};

// ============================================================================
// Main Export: Full Markdown Parser with Mentions and Highlighting
// ============================================================================

/**
 * Main function to parse chat markdown with full support for:
 * - Inline and block formatting
 * - URL linkification
 * - User mentions
 * - Search highlighting
 *
 * @param params - Configuration object
 * @param params.raws - Raw markdown text to parse
 * @param params.whiteLinks - Whether to style links as white (for dark backgrounds)
 * @param params.searchQuery - Optional search term to highlight in the text
 * @param params.unstyledMention - Whether to remove mention styling
 * @param params.onMentionClick - Callback when a mention is clicked
 * @returns React elements representing the fully formatted message
 */
export const parseChatMarkdown = ({
  raws,
  whiteLinks,
  searchQuery,
  unstyledMention,
  onMentionClick,
  mentionMemberRenderer,
}: {
  raws: string;
  whiteLinks?: boolean;
  searchQuery?: string;
  unstyledMention?: boolean;
  onMentionClick?: (userId: string) => void;
  mentionMemberRenderer?: (params: {
    userId: string;
    username: string;
  }) => React.ReactNode;
}) => {
  // Normalize line endings and preserve multiple spaces using HTML entities
  const lines = raws
    .replace(/\r\n/g, "\n")
    .replace(/( )+/g, (m) =>
      m.length === 1 ? m : m[0] + "&#32;".repeat(m.length - 1)
    )
    .split("\n");

  // Parse lines into block-level elements
  const blocks = parseBlocks(lines);

  // Render blocks with mention and highlight support
  return (
    <Mentionify
      customRenderer={onMentionClick ? mentionMemberRenderer : undefined}
      isUnstyled={unstyledMention}
    >
      <Highlightify
        searchWord={searchQuery || ""}
        highlightClassName="bg-primary-200"
      >
        {blocks.map((block) => {
          // Render headers as bold paragraphs
          if (block.type === "h") {
            return (
              <p key={getUniqueId()} className="my-0">
                <b>{renderInlineWithLinks(block.content, whiteLinks)}</b>
              </p>
            );
          }

          // Render paragraph
          if (block.type === "p") {
            return (
              <p key={getUniqueId()} className="my-0">
                {renderInlineWithLinks(block.content, whiteLinks)}
              </p>
            );
          }

          // Render unordered list
          if (block.type === "ul")
            return (
              <ul key={getUniqueId()} className="m-0 list-disc pl-5">
                {block.items.map((item) => (
                  <li key={getUniqueId()} className="my-0">
                    {renderInlineWithLinks(item, whiteLinks)}
                  </li>
                ))}
              </ul>
            );

          // Render ordered list with dynamic padding based on number width
          if (block.type === "ol") {
            const start = block.start || 1;
            const maxNumber = start + block.items.length - 1;
            const digits = String(maxNumber).length;

            // Adjust padding based on the number of digits in the largest number
            let paddingClassName = "pl-4";
            if (digits === 2) paddingClassName = "pl-6";
            else if (digits === 3) paddingClassName = "pl-8";
            else if (digits >= 4) paddingClassName = "pl-12";

            return (
              <ol
                key={getUniqueId()}
                className={cx("m-0 list-decimal", paddingClassName)}
                start={block.start || 1}
              >
                {block.items.map((item) => (
                  <li key={getUniqueId()} className="my-0">
                    {renderInlineWithLinks(item, whiteLinks)}
                  </li>
                ))}
              </ol>
            );
          }

          // Render code block with monospace font
          if (block.type === "code") {
            return (
              <pre
                key={getUniqueId()}
                className="wrap-break-word whitespace-pre-wrap"
              >
                <code>{block.content.replace(/&#32;/g, " ")}</code>
              </pre>
            );
          }

          return null;
        })}
      </Highlightify>
    </Mentionify>
  );
};
